import { Form, Formik, useField } from "collections/Form";
import BlockingForm from "collections/Form/BlockingForm";
import { DISMISS, useNotification } from "collections/Notifications";
import { OptionType, UpdateProgress } from "elements";
import {
  GroupPolicy,
  useUpdateGroupPolicyMutation,
  useCreateGroupPolicyMutation,
  GroupPolicyInput,
  PolicyType
} from "generated/graphql";
import _ from "lodash";
import { generatePath, Route, Switch, useHistory } from "polyrouter";
import { needs, useGuard } from "rbac";
import React, { useRef, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import "routes/Manage/Inventory/View/DeviceDetails.scss";
import { ProtectedRoute } from "TsRoleGuards";
import { NonNullableRequired } from "utilities/types";
import { Layout } from "views/Main/Layouts";
import { DeviceHeader } from "../DeviceHeader";
import { ErrorDisplay } from "../Settings-Shared/DeviceSettingsComponents";
import { RevertProps } from "../Settings/ProvisionedDeviceSettings";
import {
  useDeviceDetailsReadOnly,
  useDeviceSettingsRouteMatch
} from "../View/DeviceDetailsRouter";
import { useGetDesktopDeviceSettingData } from "./hooks";
import RestoreDefaultsModal from "./RestoreDefaultsModal";
import { desktopDeviceSettingsSchema } from "./schemas/desktopDeviceSettingsSchema";
import { BasicDesktopDeviceSettings } from "./schemas/types";
import {
  AdvancedSettings,
  GeneralSettings,
  LanguageSettings,
  ResetDeviceSettings,
  RingtonesVolumeSettings,
  AudioSettings,
  SensorsPresenceSettings,
  SoftphonesMediaPlayersSettings,
  VideoControlsSettings,
  DiagnosticsSettings,
  WirelessSettings
} from "./Shared/DesktopDeviceCategories";
import { DesktopDeviceSetting } from "./Shared/DesktopDeviceSetting";
import { State } from "store";
import {
  generateC10String,
  getC10Value,
  createC10QueryObj
} from "./utilities/c10";
import { getScopeRule, getPriority } from "./utilities/utils";
import {
  useDesktopDevicePolicyScopeContext,
  setDesktopDevicePolicyScope,
  DesktopDevicePolicyScope,
  CapabilitiesMap,
  constructGroupPolicyName
} from "./ProvisionedDesktopDeviceSettingsRouter";
import {
  desktopDeviceSettingChanged,
  SettingChange
} from "store/desktopDeviceSettings";
import { CombinedError, OperationContext } from "@urql/core";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

export type DesktopDeviceFormSetting = {
  tooltipText: string;
  options: OptionType[];
  accessor: string;
  type: "checkbox" | "dropdown" | "slider" | "button";
  revert?: RevertProps;
  index?: number;
  WhenHidden?: ({
    options
  }: Pick<DesktopDeviceFormSetting, "options">) => JSX.Element;
};

let requirement = needs("update:device");

export const CategorySetting = ({
  accessor,
  options,
  tooltipText,
  type,
  index
}: DesktopDeviceFormSetting) => {
  const userCanEdit = useGuard(requirement);
  const dispatch = useDispatch();
  const {
    settings,
    disableDependency,
    ...rest
  } = useGetDesktopDeviceSettingData(accessor, options, type);
  const inheritance = useSelector(
    (state: State) =>
      state.desktopDeviceSettingInheritance[generateC10String(accessor)]
  );

  const changed = useSelector(
    (state: State) => state.desktopDeviceSettings[accessor]
  );
  const { t } = useTranslation();
  const disabled = !userCanEdit || disableDependency;
  const [, { initialValue, value }] = useField(settings.name);

  useEffect(() => {
    if (changed !== SettingChange.REVERTED) {
      dispatch(
        desktopDeviceSettingChanged({
          accessor,
          change: value !== initialValue ? SettingChange.EDITED : ""
        })
      );
    }
  }, [value, initialValue, dispatch, settings.name, accessor, changed]);

  return (
    <>
      {index === 0 && (
        <div className="legend grid">
          <div className="col-4 offset-8">{t("POLICY_INHERITANCE")}</div>
        </div>
      )}
      <DesktopDeviceSetting
        key={accessor}
        accessor={accessor}
        type={type}
        changed={changed}
        settings={settings}
        disabled={disabled}
        tooltipText={tooltipText}
        inheritance={inheritance}
        {...rest}
      />
    </>
  );
};

const validate = (values: BasicDesktopDeviceSettings) => {
  if (values === null) {
    return "Null values?";
  }
  let errors: any = {};
  return errors;
};

export const defaultSettings = (): NonNullableRequired<BasicDesktopDeviceSettings> =>
  desktopDeviceSettingsSchema.default() as NonNullableRequired<
    BasicDesktopDeviceSettings
  >;

export const deepAssign = (
  target: BasicDesktopDeviceSettings,
  source: any
): BasicDesktopDeviceSettings => {
  if (_.isArray(target) || _.isArray(source)) {
    return target || source;
  }
  return _.transform(
    target,
    function(acc, value, key) {
      const fromSource = source[key];
      const targetIsObject = _.isPlainObject(value);
      const sourceIsObject = _.isPlainObject(fromSource);
      //Recurse down objects
      if (value === null || value === undefined) {
        acc[key] = fromSource;
      }
      if (targetIsObject && sourceIsObject) {
        const deepValueKeys = {
          ...fromSource,
          ...(value as object)
        };
        acc[key] = deepAssign(deepValueKeys, fromSource);
      }
      return acc;
    },
    { ...source, ...target }
  );
};
export const deepInit = (
  settings: BasicDesktopDeviceSettings
): NonNullableRequired<BasicDesktopDeviceSettings> => {
  return deepAssign(settings, defaultSettings()) as NonNullableRequired<
    BasicDesktopDeviceSettings
  >;
};

const DesktopDeviceSettings: React.FC<{
  deviceSettings: BasicDesktopDeviceSettings | null;
  onUpdate: (updatedSettings: BasicDesktopDeviceSettings) => void;
  deviceGroupPolicy?: GroupPolicy;
  refreshData: (opts?: Partial<OperationContext> | undefined) => void;
}> = ({ deviceSettings, onUpdate, deviceGroupPolicy, refreshData }) => {
  const {
    path,
    params: { deviceId }
  } = useDeviceSettingsRouteMatch();

  const [policyScope, dispatchContext] = useDesktopDevicePolicyScopeContext();
  useEffect(() => {
    dispatchContext(
      setDesktopDevicePolicyScope(DesktopDevicePolicyScope.Device)
    );
  }, [dispatchContext]);

  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [, updateGroupPolicy] = useUpdateGroupPolicyMutation();
  const [, createGroupPolicy] = useCreateGroupPolicyMutation();
  const device = useDeviceDetailsReadOnly(deviceId);

  const tenantId = useTenantId();
  const [updating, setUpdating] = useState(false);
  const history = useHistory();
  const formikRef = useRef() as any;
  const refresh = () => {
    formikRef.current.resetForm();
    history.goBack();
  };

  const unsavedSettings = useSelector(
    (state: State) => state.desktopDeviceSettings
  );
  const initialValues: NonNullableRequired<BasicDesktopDeviceSettings> = deviceSettings
    ? deepInit(deviceSettings)
    : defaultSettings();

  const onSubmit = async (values: BasicDesktopDeviceSettings) => {
    const capabilitiesToUpdate: CapabilitiesMap = [];
    const capabilitiesToDelete: string[] = [];
    Object.entries(unsavedSettings).forEach(([accessor, change]) => {
      if (change === SettingChange.REVERTED) {
        capabilitiesToDelete.push(generateC10String(accessor, false));
      } else {
        const value = getC10Value(
          values,
          generateC10String(accessor).split(".")
        );
        capabilitiesToUpdate.push({
          id: generateC10String(accessor, false),
          value
        });
      }
    });

    const capabilitiesObject = createC10QueryObj(capabilitiesToUpdate);

    const scopeRule = getScopeRule(
      policyScope.desktopDevicePolicyScope,
      {
        device: deviceId
      },
      undefined
    );
    const priority = getPriority(policyScope.desktopDevicePolicyScope);

    let errorResponse: CombinedError | undefined;

    if (deviceGroupPolicy?.id) {
      const { error } = await updateGroupPolicy({
        input: {
          id: deviceGroupPolicy?.id,
          capabilities: capabilitiesObject,
          pathsToRemove: capabilitiesToDelete
        }
      });
      errorResponse = error;
    } else {
      const policyInput: GroupPolicyInput = {
        name: constructGroupPolicyName(
          device?.hardwareModel || "",
          PolicyType.DeviceSettingsUpdate,
          policyScope.desktopDevicePolicyScope,
          undefined,
          undefined,
          undefined,
          deviceId
        ),
        priority,
        rule: {
          and: [
            { equal: { key: "tenantId", value: tenantId } },
            { equal: { key: "modelId", value: device?.model?.id } },
            ...scopeRule
          ]
        },
        capabilities: capabilitiesObject,
        pathsToRemove: capabilitiesToDelete
      };
      const { error } = await createGroupPolicy({ input: policyInput });
      errorResponse = error;
    }

    if (!errorResponse) {
      refreshData({ requestPolicy: "network-only" });
      addNotification({
        id: "save-policy-success",
        type: "success",
        message: t("DEVICE_SETTINGS_UPDATED"),
        dismiss: DISMISS.DEFAULT
      });
      const updatedSettings = _.cloneDeep(values);
      onUpdate(updatedSettings);
    } else {
      addNotification({
        type: "error",
        message: t("ERROR_SAVING_DEVICE_SETTINGS"),
        dismiss: DISMISS.DEFAULT
      });
    }
  };

  const { Content } = Layout;

  return (
    <>
      <Formik
        name="DeviceSettings"
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={desktopDeviceSettingsSchema}
        enableReinitialize={true}
        validate={values => validate(values)}
        onSubmit={onSubmit}
      >
        {({ dirty }) => (
          <BlockingForm unsavedChanges={dirty} allowedSubRoutes={["/settings"]}>
            <Form className="form pb-2">
              <DeviceHeader
                device={device}
                pageTitle={
                  <>
                    {device?.hardwareModel}
                    <p className="inline-block device-header-name">
                      {` | ${device?.name ||
                        device?.room?.name ||
                        device?.macAddress}`}
                    </p>
                  </>
                }
                hasActionButtons
              >
                <div className="sub-header-information">
                  <UpdateProgress
                    id={deviceId}
                    updating={updating}
                    setUpdating={setUpdating}
                  />
                </div>
                <ErrorDisplay />
              </DeviceHeader>
              <Content.ScrollingList>
                <Switch>
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "language"
                    })}
                    render={() => (
                      <LanguageSettings CategorySetting={CategorySetting} />
                    )}
                  />
                  <Route
                    path={generatePath(path, { deviceId, category: "general" })}
                    render={() => (
                      <GeneralSettings CategorySetting={CategorySetting} />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "ringtones-volume"
                    })}
                    render={() => (
                      <RingtonesVolumeSettings
                        CategorySetting={CategorySetting}
                      />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "audio"
                    })}
                    render={() => (
                      <AudioSettings CategorySetting={CategorySetting} />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "sensors-presence"
                    })}
                    render={() => (
                      <SensorsPresenceSettings
                        CategorySetting={CategorySetting}
                      />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "softphones-media-players"
                    })}
                    render={() => (
                      <SoftphonesMediaPlayersSettings
                        CategorySetting={CategorySetting}
                      />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "wireless"
                    })}
                    render={() => (
                      <WirelessSettings CategorySetting={CategorySetting} />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "advanced"
                    })}
                    render={() => (
                      <AdvancedSettings CategorySetting={CategorySetting} />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "video-controls"
                    })}
                    render={() => (
                      <VideoControlsSettings
                        CategorySetting={CategorySetting}
                      />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "diagnostics"
                    })}
                    render={() => (
                      <DiagnosticsSettings CategorySetting={CategorySetting} />
                    )}
                  />
                  <Route
                    path={generatePath(path, {
                      deviceId,
                      category: "reset-device"
                    })}
                    component={ResetDeviceSettings}
                  />
                  <Route component={GeneralSettings} />
                </Switch>
              </Content.ScrollingList>
            </Form>
          </BlockingForm>
        )}
      </Formik>
      <ProtectedRoute nest path="/confirm" requirement="update:device">
        <RestoreDefaultsModal deviceId={deviceId} refresh={refresh} />
      </ProtectedRoute>
    </>
  );
};

export default DesktopDeviceSettings;
